"use client"

import React from "react"

import { useState, useEffect } from "react"
import { usePathname } from "next/navigation"
import Link from "next/link"
import { Menu, User, ChevronRight, Dog, Bird, Pill, Tag, X, Home, ShoppingCart } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { cn } from "@/lib/utils"
import useMobile from "@/hooks/use-mobile"
import { CatIcon, FishIcon, HamsterIcon, LizardIcon } from "./custom-icons"
import { useAuth } from "@/context/AuthContext"

// Define types for the API response
type Category = {
  id: number
  name: string
  slug: string
  parentId: number | null
  image: string | null
}

// Define the type for subcategories
type SubcategoryStructure = Record<string, string[]>
type Subcategories = Record<string, SubcategoryStructure>

// Define the type for main links
interface MainLink {
  name: string
  href: string
  icon?: React.ElementType
}

// Define the type for a category item
interface CategoryItem {
  id: number
  name: string
  href: string
  icon: React.ElementType
  isHighlighted?: boolean
}

// Map category names to icons
const categoryIcons: Record<string, any> = {
  Кучета: Dog,
  Котки: CatIcon,
  Риби: FishIcon,
  Птици: Bird,
  "Малки животни": HamsterIcon,
  Тераристика: LizardIcon,
  "Ветеринарна аптека": Pill,
  ПРОМОЦИИ: Tag,
  // Default icon for any category not explicitly mapped
  default: Dog,
}

export function Navigation() {
  const pathname = usePathname()
  const isMobile = useMobile()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [subcategories, setSubcategories] = useState<Subcategories>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [apiResponse, setApiResponse] = useState<any>(null)

  // Use AuthContext to determine if the user is authenticated.
  const { user } = useAuth()
  // If the user exists, link to profile; otherwise, send to login.
  const profileLink = user ? "/customer/profile" : "/customer/login"

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  const mainLinks: MainLink[] = [
    { name: "Начало", href: "/", icon: Home },
    { name: "За нас", href: "/about" },
    { name: "Контакти", href: "/contact" },
    { name: "Въпроси и отговори", href: "/vuprosi-i-otgovori" },
    { name: "Магазини", href: "/stores" },
    { name: "Блог", href: "/blog" },
  ]

  // Fallback subcategories if API doesn't provide proper structure
  const fallbackSubcategories: Subcategories = {
    Кучета: {
      "Храна за Кучета": [
        "Суха храна",
        "Консервирана храна",
        "Grain Free серии",
      ],
      Аксесоари: [
        "Поводи и нагръдници",
        "Купички и поставки",
      ],
    },
  }

  // Fetch categories and subcategories from the API
  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoading(true)
        const response = await fetch("/api/categories")

        if (!response.ok) {
          throw new Error("Failed to fetch categories")
        }

        const data = await response.json()
        // Save full API response for debugging
        setApiResponse(data)

        const allCategories = data.categories

        // Map top-level categories (those with no parentId)
        const mainCategories = allCategories
          .filter((cat: Category) => cat.parentId === null)
          .map((cat: Category) => ({
            id: cat.id,
            name: cat.name,
            href: `/${cat.slug || cat.name.toLowerCase().replace(/\s+/g, "-")}`,
            icon: categoryIcons[cat.name] || categoryIcons.default,
            isHighlighted: cat.name === "ПРОМОЦИИ",
          }))

        setCategories(mainCategories)

        // Process subcategories
        const subcategoriesData: Subcategories = {}

        mainCategories.forEach((mainCat: CategoryItem) => {
          const categoryName = mainCat.name
          subcategoriesData[categoryName] = {}

          // Find second-level categories (direct children of main category)
          const secondLevelCategories = allCategories.filter(
            (cat: Category) => cat.parentId === mainCat.id
          )

          // For each second-level category
          secondLevelCategories.forEach((secondLevelCat: Category) => {
            const sectionName = secondLevelCat.name

            // Find third-level categories (children of second-level)
            const thirdLevelItems = allCategories
              .filter((cat: Category) => cat.parentId === secondLevelCat.id)
              .map((cat: Category) => cat.name)

            // Add this section with its items
            if (thirdLevelItems.length > 0) {
              if (!subcategoriesData[categoryName]) {
                subcategoriesData[categoryName] = {};
              }
              subcategoriesData[categoryName][sectionName] = thirdLevelItems
            } else {
              // If no third-level items, use the second-level as an item itself
              // This handles cases where the hierarchy is not fully three levels deep
              if (!subcategoriesData[categoryName]) {
                subcategoriesData[categoryName] = {};
              }
              if (!subcategoriesData[categoryName]["Общи"]) {
                subcategoriesData[categoryName]["Общи"] = []
              }
              subcategoriesData[categoryName]["Общи"].push(sectionName)
            }
          })

          // If no subcategories were found for this main category, remove its entry
          if (Object.keys(subcategoriesData[categoryName] || {}).length === 0) {
            delete subcategoriesData[categoryName]
          }
        })

        console.log("Generated subcategories:", subcategoriesData)

        // If we have structured subcategories, use them; otherwise, fall back to mock data for testing
        if (Object.keys(subcategoriesData).length > 0) {
          setSubcategories(subcategoriesData)
        } else {
          // Use fallback data for testing if no subcategories found
          setSubcategories(fallbackSubcategories)
          console.warn("No subcategories found in API data, using fallback data")
        }
      } catch (err) {
        console.error("Error fetching categories:", err)
        setError("Failed to load categories")

        // Fallback to some default categories in case of error
        setCategories([
          { id: 1, name: "Кучета", href: "/kucheta", icon: Dog },
          { id: 2, name: "Котки", href: "/kotki", icon: CatIcon },
          { id: 3, name: "ПРОМОЦИИ", href: "/promotsii", icon: Tag, isHighlighted: true },
        ])

        // Use fallback subcategories in case of error
        setSubcategories(fallbackSubcategories)
      } finally {
        setLoading(false)
      }
    }

    fetchCategories()
  }, [])

  // For debugging - inspect API response and subcategories
  useEffect(() => {
    if (apiResponse) {
      console.log("API Response:", apiResponse)
      console.log("Processed Subcategories:", subcategories)
    }
  }, [apiResponse, subcategories])

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden lg:block bg-blue-600 text-white shadow-lg sticky top-[72px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-1">
              {/* Categories Dropdown */}
              <div className="relative group">
                <Button variant="ghost" className="text-white hover:bg-blue-700">
                  <Menu className="h-5 w-5 mr-2" />
                  <span className="font-medium">Категории</span>
                </Button>
                <div className="absolute left-0 top-full z-50 hidden group-hover:block">
                  <div className="w-[280px] bg-white rounded-lg shadow-lg py-2 mt-1">
                    {loading ? (
                      <div className="p-4 text-center text-gray-500">Loading...</div>
                    ) : error ? (
                      <div className="p-4 text-center text-red-500">{error}</div>
                    ) : (
                      categories.map((category) => (
                        <div key={category.id} className="relative group/item">
                          <Link
                            href={category.href}
                            className={cn(
                              "w-full px-4 py-2 text-left flex items-center justify-between hover:bg-gray-100",
                              category.isHighlighted ? "text-blue-600 font-medium" : "text-gray-700",
                            )}
                          >
                            <span className="flex items-center gap-2">
                              {category.icon && React.createElement(category.icon, { className: "h-5 w-5" })}

                              <span className="text-sm">{category.name}</span>
                            </span>
                            {subcategories[category.name] && <ChevronRight className="h-4 w-4" />}
                          </Link>
                          {subcategories[category.name] && (
                            <div className="absolute left-full top-0 hidden group-hover/item:block w-[700px] bg-white shadow-lg rounded-r-lg z-50">
                              <div className="grid grid-cols-3 gap-6 p-6">
                                {Object.entries(subcategories[category.name] ?? {}).map(([section, items]) => (
                                  <div key={section}>
                                    <h3 className="font-medium text-blue-600 mb-2 text-sm">{section}</h3>
                                    <ul className="space-y-1">
                                      {items.map((item) => (
                                        <li key={item}>
                                          <Link href="#" className="text-xs text-gray-600 hover:text-blue-600">
                                            {item}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
              {mainLinks.map(({ name, href, icon: Icon }) => (
                <Button key={href} variant="ghost" asChild className="text-white hover:bg-blue-700">
                  <Link href={href} className="flex items-center gap-1">
                    {Icon && <Icon className="h-4 w-4 mr-1" />}
                    <span>{name}</span>
                  </Link>
                </Button>
              ))}
            </div>
            <Button variant="ghost" asChild className="text-white hover:bg-blue-700">
              <Link href={profileLink} className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <span>MyLV-Vet</span>
              </Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="flex justify-between items-center px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(true)}
            className="flex flex-col items-center gap-1 h-auto py-2"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
            <span className="text-xs">Меню</span>
          </Button>

          <Link href="/">
            <Button
              variant="ghost"
              size="sm"
              className="flex flex-col items-center gap-1 h-auto py-2"
              aria-label="Home"
            >
              <Home className="h-5 w-5" />
              <span className="text-xs">Начало</span>
            </Button>
          </Link>

          <Link href="/cart">
            <Button
              variant="ghost"
              size="sm"
              className="flex flex-col items-center gap-1 h-auto py-2"
              aria-label="Cart"
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="text-xs">Кошница</span>
            </Button>
          </Link>
        </div>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent
            side="left"
            className="w-[85vw] sm:w-[350px] p-0 bg-white"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {/* Mobile Menu Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <Link href="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Dog className="h-6 w-6 text-blue-500" />
                <span className="font-bold text-xl">LV-Vet</span>
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <X className="h-6 w-6" />
              </Button>
            </div>

            {/* Phone Number */}
            <div className="px-6 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Телефон за поръчки:</span>
                <a href="tel:070019519" className="text-blue-600 font-semibold">
                  0700 19 519
                </a>
              </div>
            </div>

            {/* Main Links */}
            <div className="px-6 py-3 border-b border-gray-200">
              <div className="grid grid-cols-2 gap-2">
                {mainLinks.map(({ name, href, icon: Icon }) => (
                  <Link
                    key={href}
                    href={href}
                    className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    <span>{name}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Accordion for Categories */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(100vh-200px)]">
              <h3 className="font-medium mb-2">Категории</h3>
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : error ? (
                <div className="p-4 text-center text-red-500">{error}</div>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {categories.map((category) => (
                    <AccordionItem key={category.id} value={category.name}>
                      <AccordionTrigger
                        className={cn(
                          "py-2 text-sm flex items-center gap-2",
                          category.isHighlighted ? "text-blue-600 font-medium" : "",
                        )}
                      >
                        {category.icon && React.createElement(category.icon, { className: "h-5 w-5" })}
                        <span>{category.name}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm pl-5">
                        {subcategories[category.name] ? (
                          <div className="space-y-4">
                            {Object.entries(subcategories[category.name] ?? {}).map(([section, items]) => (
                              <div key={section} className="space-y-2">
                                <h4 className="font-medium text-blue-600 mb-2">{section}</h4>
                                <ul className="space-y-1">
                                  {items.map((item) => (
                                    <li key={item}>
                                      <Link
                                        href="#"
                                        className="text-xs text-gray-600 hover:text-blue-600 block py-1"
                                        onClick={() => setMobileMenuOpen(false)}
                                      >
                                        {item}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="py-2 text-sm text-gray-500">No subcategories available</div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  )
}